import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Image,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { FaWallet, FaQrcode, FaExternalLinkAlt, FaSignOutAlt, FaCopy } from 'react-icons/fa';
import useWallet, { WALLET_STATUS } from '../store/useWallet';
import { COLORS } from '../utils/constants';

const XamanWalletConnect = () => {
  const {
    status,
    address,
    xrpBalance,
    xgcBalance,
    errorMessage,
    initialize,
    connectXaman,
    disconnect,
    fetchBalances,
  } = useWallet();
  
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  // Initialize the wallet store on component mount
  useEffect(() => {
    const init = async () => {
      await initialize();
    };
    
    init();
    
    // Clean up on unmount
    return () => {
      useWallet.getState().cleanup();
    };
  }, [initialize]);
  
  // Handle connect button click
  const handleConnect = async () => {
    try {
      // Reset any previous errors
      setQrCodeUrl('');
      onOpen(); // Open the modal first
      
      console.log('Initiating wallet connection...');
      const result = await connectXaman();
      
      if (result.success) {
        console.log('Successfully initiated connection, displaying QR code');
        setQrCodeUrl(result.qrUrl);
        toast({
          title: 'Connection Initiated',
          description: 'Please scan the QR code with your Xaman wallet app',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.error('Failed to connect to wallet:', result.error);
        throw new Error(result.error?.message || 'Failed to connect wallet');
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      toast({
        title: 'Connection Error',
        description: `${error.message}. Please check your API key configuration.`,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
      onClose();
    }
  };
  
  // Handle disconnect button click
  const handleDisconnect = () => {
    disconnect();
    toast({
      title: 'Wallet Disconnected',
      description: 'Your wallet has been disconnected',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Handle copy address to clipboard
  const handleCopyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast({
        title: 'Address Copied',
        description: 'Wallet address copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  };
  
  // Handle refresh balances
  const handleRefreshBalances = async () => {
    if (address) {
      const result = await fetchBalances();
      
      if (!result.success) {
        toast({
          title: 'Error',
          description: 'Failed to refresh balances',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };
  
  // Close the modal when wallet is connected
  useEffect(() => {
    if (status === WALLET_STATUS.CONNECTED) {
      onClose();
    }
  }, [status, onClose]);
  
  // Show error toast when an error occurs
  useEffect(() => {
    if (errorMessage && status === WALLET_STATUS.ERROR) {
      toast({
        title: 'Wallet Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [errorMessage, status, toast]);
  
  return (
    <>
      {/* Wallet Connection Button */}
      {status === WALLET_STATUS.DISCONNECTED && (
        <Button
          leftIcon={<FaWallet />}
          colorScheme="green"
          bg={COLORS.primary}
          color="black"
          onClick={handleConnect}
          size="md"
          fontWeight="bold"
        >
          Connect Wallet
        </Button>
      )}
      
      {/* Connecting State */}
      {status === WALLET_STATUS.CONNECTING && (
        <Button
          leftIcon={<Spinner size="sm" />}
          colorScheme="green"
          bg={COLORS.primary}
          color="black"
          isLoading
          loadingText="Connecting..."
          size="md"
          fontWeight="bold"
          cursor="not-allowed"
        >
          Connecting...
        </Button>
      )}
      
      {/* Connected State - Wallet Info */}
      {status === WALLET_STATUS.CONNECTED && address && (
        <Flex
          direction="column"
          p={3}
          borderRadius="md"
          border="1px solid"
          borderColor={COLORS.primary}
          bg="rgba(0, 0, 0, 0.7)"
        >
          <HStack spacing={4} justify="space-between" mb={2}>
            <HStack>
              <FaWallet color={COLORS.primary} />
              <Text fontWeight="bold">Wallet Connected</Text>
            </HStack>
            <Button
              size="sm"
              variant="ghost"
              color={COLORS.primary}
              leftIcon={<FaSignOutAlt />}
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          </HStack>
          
          <HStack spacing={2} mb={2}>
            <Text fontSize="sm" noOfLines={1} maxW="150px">
              {address.substring(0, 8)}...{address.substring(address.length - 4)}
            </Text>
            <Box cursor="pointer" onClick={handleCopyAddress}>
              <FaCopy size="14px" color={COLORS.primary} />
            </Box>
          </HStack>
          
          <VStack align="flex-start" spacing={1}>
            <HStack justify="space-between" width="100%">
              <Text fontSize="sm">XRP Balance:</Text>
              <HStack spacing={1}>
                <Text fontWeight="bold">{xrpBalance.toFixed(2)}</Text>
                <Text fontSize="sm">XRP</Text>
              </HStack>
            </HStack>
            
            <HStack justify="space-between" width="100%">
              <Text fontSize="sm">XGC Balance:</Text>
              <HStack spacing={1}>
                <Text fontWeight="bold">{xgcBalance.toFixed(2)}</Text>
                <Text fontSize="sm" color={COLORS.primary}>XGC</Text>
              </HStack>
            </HStack>
          </VStack>
          
          <Button
            size="xs"
            variant="outline"
            mt={2}
            borderColor={COLORS.primary}
            color={COLORS.primary}
            onClick={handleRefreshBalances}
          >
            Refresh Balances
          </Button>
        </Flex>
      )}
      
      {/* Wallet Connection Modal with QR Code */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg="rgba(0, 0, 0, 0.9)"
          borderColor={status === WALLET_STATUS.ERROR ? "red.500" : COLORS.primary}
          borderWidth="1px"
          color="white"
        >
          <ModalHeader>Connect Xaman Wallet</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="center">
              {status === WALLET_STATUS.ERROR ? (
                <Box 
                  p={4} 
                  bg="rgba(255, 0, 0, 0.1)" 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor="red.500"
                  width="100%"
                >
                  <Text color="red.400" fontWeight="bold" mb={2}>Connection Error</Text>
                  <Text fontSize="sm">{errorMessage || "Failed to connect to Xaman. Please check your configuration."}</Text>
                  <Button 
                    mt={4} 
                    size="sm" 
                    colorScheme="red" 
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </Box>
              ) : (
                <>
                  <Text fontSize="sm" textAlign="center">
                    Scan this QR code with your Xaman wallet app or click the link below to open the app
                  </Text>
                  
                  {qrCodeUrl ? (
                    <Box borderRadius="md" overflow="hidden" p={4} bg="white">
                      <Image src={qrCodeUrl} alt="Xaman QR Code" maxW="250px" />
                    </Box>
                  ) : (
                    <VStack>
                      <Spinner color={COLORS.primary} size="xl" />
                      <Text fontSize="xs" color="gray.300" mt={2}>Connecting to Xaman API...</Text>
                    </VStack>
                  )}
                  
                  <Button
                    leftIcon={<FaExternalLinkAlt />}
                    rightIcon={<FaQrcode />}
                    colorScheme="green"
                    bg={COLORS.primary}
                    color="black"
                    size="md"
                    width="100%"
                    isDisabled={!qrCodeUrl}
                    onClick={() => qrCodeUrl && window.open('https://xumm.app/', '_blank')}
                  >
                    Open in Xaman App
                  </Button>
                  
                  <Badge colorScheme="green" mt={2}>
                    {qrCodeUrl ? 'Waiting for wallet approval...' : 'Preparing connection...'}
                  </Badge>
                </>
              )}
              
              <Box mt={4} px={2} width="100%">
                <Divider mb={4} />
                <Text fontSize="xs" color="gray.400">
                  Tip: Make sure your API key is correctly configured in the .env file. If you're experiencing issues, try refreshing the page.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default XamanWalletConnect; 